
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, ShoppingCart, Bell, LogOut, Wallet, Menu, Copy, ChevronDown, CheckCircle, RefreshCw, QrCode } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useCart } from '../../context/CartContext';
import { useWallet } from '../../hooks/useWallet';
import { ViewState } from '../../types';
import { Button } from '../ui/Button';
import { WalletIcon } from '../ui/ChainIcons';
import { navigationConfig } from '../../constants';

interface HeaderProps {
    currentView: ViewState;
    onNavigate: (view: ViewState) => void;
    onConnectWallet?: () => void;
    onToggleSidebar: () => void;
}

export const Header: React.FC<HeaderProps> = ({ currentView, onNavigate, onConnectWallet, onToggleSidebar }) => {
    const { theme, toggleTheme } = useTheme();
    const { items } = useCart();
    const { walletAddress, disconnect, walletType, balances, connect } = useWallet();
    const [isWalletMenuOpen, setWalletMenuOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const walletMenuRef = useRef<HTMLDivElement>(null);
    
    const currentNavItem = navigationConfig.find(item => item.view === currentView);
    const pageTitle = currentNavItem ? currentNavItem.label : currentView.replace('-', ' ');

    const totalBalance = balances.reduce((acc, token) => acc + (token.balance * token.priceUsd), 0);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (walletMenuRef.current && !walletMenuRef.current.contains(event.target as Node)) {
                setWalletMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCopy = () => {
        if (walletAddress) {
            navigator.clipboard.writeText(walletAddress);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        }
    };

    const handleQuickWalletConnect = () => {
        connect('walletconnect');
    };

    return (
        <header className="hidden md:flex bg-white/80 dark:bg-[#0A0B0F]/90 backdrop-blur-xl border-b border-gray-200 dark:border-[#1a1a1a] sticky top-0 z-30 px-8 py-5 justify-between items-center transition-colors duration-300">
            <div className="flex items-center gap-4">
                <h2 className="text-lg font-serif font-bold text-[#0A0B0F] dark:text-white capitalize tracking-tight">
                    {pageTitle}
                </h2>
            </div>
            
            <div className="flex items-center gap-4 ml-auto">
               <button 
                  onClick={toggleTheme}
                  className="p-2.5 rounded-lg text-gray-400 hover:text-[#0A0B0F] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
               >
                   {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
               </button>

               <button 
                  onClick={() => onNavigate('marketplace')}
                  className="p-2.5 rounded-lg text-gray-400 hover:text-[#0A0B0F] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors relative"
               >
                  <ShoppingCart size={20} />
                  {items.length > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#0A0B0F] dark:bg-white text-white dark:text-[#0A0B0F] rounded-full text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-[#0A0B0F]">
                          {items.length}
                      </span>
                  )}
               </button>

               <div className="hidden lg:flex flex-col items-end mr-2 border-r border-gray-200 dark:border-white/10 pr-6">
                  <span className="text-[9px] text-gray-400 uppercase tracking-[0.2em] font-bold">Consolidated Net</span>
                  <span className="font-serif text-lg text-[#0A0B0F] dark:text-white font-bold tracking-tight">${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
               </div>
               
               <button className="p-2.5 text-gray-400 hover:text-[#0A0B0F] dark:hover:text-white transition-colors relative">
                  <Bell size={20} />
                  <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-nobel-gold rounded-full border-2 border-white dark:border-[#0A0B0F]"></span>
               </button>

               {walletAddress ? (
                 <div className="relative" ref={walletMenuRef}>
                    <button 
                        onClick={() => setWalletMenuOpen(!isWalletMenuOpen)}
                        className="flex items-center gap-3 px-5 h-14 bg-white dark:bg-[#15171C] border border-stone-200 dark:border-[#2D3142] rounded-xl hover:border-nobel-gold/50 transition-all shadow-sm group"
                    >
                        <WalletIcon wallet={walletType || ''} className="w-6 h-6 rounded-md" />
                        <span className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-gray-200">
                            {walletAddress.slice(0, 4)}...{walletAddress.slice(-4)}
                        </span>
                        <ChevronDown size={14} className={`text-stone-400 transition-transform ${isWalletMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                        {isWalletMenuOpen && (
                            <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#1A1D23] border border-stone-200 dark:border-[#2D3142] rounded-2xl shadow-2xl z-50 overflow-hidden"
                            >
                                <div className="p-4 border-b border-stone-100 dark:border-white/5 bg-gray-50 dark:bg-white/[0.02]">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest capitalize">{walletType} connected</span>
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                    </div>
                                    <div className="flex items-center gap-2 group cursor-pointer" onClick={handleCopy}>
                                        <span className="text-[11px] font-mono text-[#0A0B0F] dark:text-gray-300 truncate">{walletAddress}</span>
                                        {copied ? <CheckCircle size={12} className="text-green-500" /> : <Copy size={12} className="text-stone-400 group-hover:text-nobel-gold transition-colors" />}
                                    </div>
                                </div>
                                <div className="p-2">
                                    <button 
                                        onClick={() => { onNavigate('wallet'); setWalletMenuOpen(false); }}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-white/5 rounded-xl transition-colors"
                                    >
                                        <Wallet size={16} /> Wallet Dashboard
                                    </button>
                                    <button className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-white/5 rounded-xl transition-colors">
                                        <RefreshCw size={16} /> Sync Ledger
                                    </button>
                                    <div className="h-px bg-stone-100 dark:bg-white/5 my-1 mx-2" />
                                    <button 
                                        onClick={disconnect}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
                                    >
                                        <LogOut size={16} /> Terminate Session
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                 </div>
               ) : (
                 <div className="flex items-center gap-2">
                    <button 
                        onClick={handleQuickWalletConnect}
                        className="p-2.5 h-14 w-14 flex items-center justify-center rounded-xl text-stone-400 hover:text-nobel-gold hover:bg-nobel-gold/5 border border-stone-200 dark:border-white/10 transition-all shadow-sm"
                        title="Quick Connect (WalletConnect)"
                    >
                        <QrCode size={20} />
                    </button>
                 </div>
               )}
            </div>
        </header>
    );
};
